import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  analyticsRidersFilterApplied,
  analyticsRidersFilterCleared,
  analyticsRidersPageChanged,
  analyticsRidersSortApplied,
  analyticsRidersSortCleared,
} from '@rideiq/api/services/Analytics';
import { getRiders } from '@rideiq/api/services/Riders';
import * as Constants from '@rideiq/constants';
import { stringToValues } from '@rideiq/reports/util/queryToFilter';
import { useRidersTablePrefsStore } from '@rideiq/riders/stores/ridersTablePrefsStore';
import type { RidersQueryParams } from '@rideiq/riders/types';
import { leaveBreadcrumb, reportError } from '@rideiq/utils/errorReporting/error';
import { useInfiniteQuery } from '@tanstack/react-query';
import { debounce } from 'lodash';

export const RIDERS_QUERY_KEY = 'riders';

// Carries the HTTP status alongside the message so the isError effect below can
// tell an expected session-expiry (401) apart from a real failure worth reporting.
class RidersLoadError extends Error {
  status?: number;

  constructor(message: string, status?: number) {
    super(message);
    this.name = 'RidersLoadError';
    this.status = status;
  }
}

const buildApiParams = (params: RidersQueryParams, page: number) => ({
  q: params.query,
  perPage: params.perPage,
  page,
  state: params.state,
  byRiderSearchAlpha: params.byRiderSearchAlpha,
  ...(params.invoiceGroupIds && {
    invoiceGroupIds: params.invoiceGroupIds.includes('|')
      ? stringToValues(params.invoiceGroupIds)
      : params.invoiceGroupIds.split(',').filter(Boolean),
  }),
  ...(params.sortBy && { sortField: params.sortBy, sortDir: params.sortDir ?? 'asc' }),
  ...(params.categories?.length && { categories: params.categories }),
  ...(params.safetySettings?.length && { safetySettings: params.safetySettings }),
  ...(params.services?.length && { serviceIds: params.services }),
});

export const useRidersQuery = () => {
  const perPage = useRidersTablePrefsStore((state) => state.perPage);
  const setPersistedPerPage = useRidersTablePrefsStore((state) => state.setPerPage);

  const [queryParams, setQueryParams] = useState<RidersQueryParams>({
    page: 1,
    perPage,
    query: undefined,
    state: 'active',
    byRiderSearchAlpha: false,
    invoiceGroupIds: undefined,
    sortBy: null,
    sortDir: null,
    categories: [],
    safetySettings: [],
    services: [],
  });

  const { data, isFetching, isError, error, refetch, fetchNextPage } = useInfiniteQuery({
    queryKey: [RIDERS_QUERY_KEY, queryParams],
    queryFn: async ({ pageParam, signal }) => {
      const response = await getRiders(buildApiParams(queryParams, pageParam as number), signal);

      if (response.type === 'error') {
        const { status } = response;
        const fallbackMessage = status ? `Failed to load riders (HTTP ${status})` : 'Failed to load riders';

        throw new RidersLoadError(response.error.errors[0] ?? fallbackMessage, status);
      }

      return response.data;
    },
    // queryParams.page changes the query key, so a fresh query must start at that page.
    initialPageParam: queryParams.page,
    getNextPageParam: (lastPage, allPages) =>
      (lastPage.riders?.length ?? 0) === Constants.ITEM_LIMIT_PER_PAGE ? allPages.length + 1 : undefined,
  });

  useEffect(() => {
    if (isError) {
      const status = error instanceof RidersLoadError ? error.status : undefined;

      // AxiosErrorBoundary already handles session-expiry redirects for 401s;
      // avoid a duplicate, noisy Bugsnag report for an expected auth flow.
      if (status === 401) {
        return;
      }

      leaveBreadcrumb('Riders search failed', { query: queryParams.query, state: queryParams.state, status }, 'error');
      reportError(error instanceof Error ? error : new Error('Failed to load riders'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isError, error]);

  const riders = data?.pages.flatMap((page) => page.riders ?? []) ?? [];
  const totalCount = data?.pages[data.pages.length - 1]?.meta?.totalCount ?? 0;

  const handlePagination = useCallback(() => {
    fetchNextPage();
  }, [fetchNextPage]);

  const handlePageChange = useCallback(
    ({ page, perPage: nextPerPage }: { page: number; perPage: number }, isPaginated?: boolean) => {
      if (isPaginated) {
        window.scrollTo(0, 0);
        const totalPages = Math.ceil(totalCount / nextPerPage);

        analyticsRidersPageChanged(page, totalPages, totalCount);
      }
      if (nextPerPage !== queryParams.perPage) {
        setPersistedPerPage(nextPerPage);
      }
      setQueryParams((prev) => ({ ...prev, page, perPage: nextPerPage }));
    },
    [totalCount, queryParams.perPage, setPersistedPerPage]
  );

  const handleQuery = useCallback(
    (
      query: string,
      options?: { state?: 'active' | 'deactivated' | 'paused' | 'uploads'; invoiceGroups?: string | null },
      byRiderAlpha?: boolean
    ) => {
      const state = options?.state;

      if (!query || query.length >= Constants.MINIMUM_SEARCH_STRING_LENGTH || state || byRiderAlpha) {
        window.scrollTo(0, 0);
        setQueryParams((prevQueryParams) => {
          const nextState =
            state && ['active', 'deactivated', 'paused', 'uploads'].includes(state) ? state : prevQueryParams.state;
          const isTabSwitch = nextState !== prevQueryParams.state;
          let nextInvoiceGroupIds = prevQueryParams.invoiceGroupIds;

          if (options?.invoiceGroups !== undefined) {
            nextInvoiceGroupIds = options.invoiceGroups;
          } else if (isTabSwitch) {
            nextInvoiceGroupIds = null;
          }

          return {
            query: query !== prevQueryParams.query ? query : prevQueryParams.query,
            page: 1,
            perPage: prevQueryParams.perPage,
            state: nextState,
            byRiderSearchAlpha: byRiderAlpha || (query?.length > 1 && byRiderAlpha),
            invoiceGroupIds: nextInvoiceGroupIds,
            sortBy: isTabSwitch ? null : prevQueryParams.sortBy ?? null,
            sortDir: isTabSwitch ? null : prevQueryParams.sortDir ?? null,
            categories: isTabSwitch ? [] : prevQueryParams.categories ?? [],
            safetySettings: isTabSwitch ? [] : prevQueryParams.safetySettings ?? [],
            services: isTabSwitch ? [] : prevQueryParams.services ?? [],
          };
        });
      }
    },
    []
  );

  const debounceHandleQuery = useMemo(() => debounce(handleQuery, 400), [handleQuery]);

  useEffect(() => {
    return () => {
      debounceHandleQuery.cancel();
    };
  }, [debounceHandleQuery]);

  const handleSort = useCallback((column: string) => {
    setQueryParams((prev) => {
      if (prev.sortBy === column) {
        if (prev.sortDir === 'asc') {
          analyticsRidersSortApplied(column, 'desc');

          return { ...prev, sortDir: 'desc', page: 1 };
        }
        analyticsRidersSortCleared(column, prev.sortDir ?? 'desc');

        return { ...prev, sortBy: null, sortDir: null, page: 1 };
      }
      analyticsRidersSortApplied(column, 'asc');

      return { ...prev, sortBy: column, sortDir: 'asc', page: 1 };
    });
  }, []);

  const clearSort = useCallback(() => {
    setQueryParams((prev) => {
      if (prev.sortBy) {
        analyticsRidersSortCleared(prev.sortBy, prev.sortDir ?? 'asc');
      }

      return { ...prev, sortBy: null, sortDir: null, page: 1 };
    });
  }, []);

  const setActiveFilters = useCallback((selections: Record<string, string[]>) => {
    const activeSelections = Object.fromEntries(Object.entries(selections).filter(([, values]) => values.length > 0));

    if (Object.keys(activeSelections).length > 0) {
      analyticsRidersFilterApplied(activeSelections);
    }
    setQueryParams((prev) => ({
      ...prev,
      categories: selections.categories ?? [],
      safetySettings: selections.safetySettings ?? [],
      services: selections.services ?? [],
      invoiceGroupIds: selections.invoiceGroups?.length ? selections.invoiceGroups.join(',') : null,
      page: 1,
    }));
  }, []);

  const clearAllFilters = useCallback(() => {
    setQueryParams((prev) => {
      const previousFilters: Record<string, string[]> = {
        categories: prev.categories ?? [],
        safetySettings: prev.safetySettings ?? [],
        services: prev.services ?? [],
        invoiceGroups: prev.invoiceGroupIds ? prev.invoiceGroupIds.split(',').filter(Boolean) : [],
      };

      if (Object.values(previousFilters).some((v) => v.length > 0)) {
        analyticsRidersFilterCleared(previousFilters);
      }

      return { ...prev, categories: [], safetySettings: [], services: [], invoiceGroupIds: null, page: 1 };
    });
  }, []);

  // Same filter set as the index GET, minus pagination/sort/search — keeps bulk actions in sync with the table.
  const bulkActionQueryParams = useMemo(() => {
    const {
      q: _q,
      page: _page,
      perPage: _perPage,
      sortField: _sortField,
      sortDir: _sortDir,
      ...rest
    } = buildApiParams(queryParams, queryParams.page);

    return rest;
  }, [queryParams]);

  // queryParams is part of the queryKey, so any state change here triggers a
  // fresh TanStack fetch automatically — consumers don't need to call fetchRiders.
  return {
    riders,
    queryParams,
    bulkActionQueryParams,
    totalCount,
    isLoading: isFetching,
    isError,
    fetchRiders: refetch,
    handlePagination,
    handlePageChange,
    handleQuery,
    debounceHandleQuery,
    handleSort,
    clearSort,
    setActiveFilters,
    clearAllFilters,
  };
};
